import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import styled, { keyframes } from 'styled-components';

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface NotificationContextType {
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id'>) => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  showSuccess: (message: string, title?: string) => void;
  showError: (message: string, title?: string) => void;
  showWarning: (message: string, title?: string) => void;
  showInfo: (message: string, title?: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const NotificationContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  max-width: 400px;
  width: 100%;
  pointer-events: none;

  @media (max-width: 768px) {
    top: 10px;
    right: 10px;
    left: 10px;
    max-width: none;
  }
`;

const NotificationItem = styled.div<{ 
  type: 'success' | 'error' | 'warning' | 'info';
  isExiting?: boolean;
}>`
  background: var(--surface);
  border: 2px solid;
  border-color: ${props => {
    switch (props.type) {
      case 'success': return '#28a745';
      case 'error': return '#dc3545';
      case 'warning': return '#ffc107';
      case 'info': return '#17a2b8';
      default: return 'var(--border)';
    }
  }};
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, ${props => 
    document.documentElement.getAttribute('data-theme') === 'dark' ? '0.3' : '0.15'
  });
  pointer-events: all;
  animation: ${props => props.isExiting ? slideOut : slideIn} 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => {
      switch (props.type) {
        case 'success': return 'linear-gradient(90deg, #28a745, #20c997)';
        case 'error': return 'linear-gradient(90deg, #dc3545, #e74c3c)';
        case 'warning': return 'linear-gradient(90deg, #ffc107, #fd7e14)';
        case 'info': return 'linear-gradient(90deg, #17a2b8, #6f42c1)';
        default: return 'var(--primary)';
      }
    }};
  }
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const NotificationIcon = styled.div<{ type: 'success' | 'error' | 'warning' | 'info' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => {
    switch (props.type) {
      case 'success': return '#28a745';
      case 'error': return '#dc3545';
      case 'warning': return '#ffc107';
      case 'info': return '#17a2b8';
      default: return 'var(--text)';
    }
  }};
  font-weight: 600;
  font-size: 0.9rem;

  &::before {
    content: '${props => {
      switch (props.type) {
        case 'success': return '✅';
        case 'error': return '❌';
        case 'warning': return '⚠️';
        case 'info': return 'ℹ️';
        default: return '📢';
      }
    }}';
    font-size: 1.1rem;
  }
`;

const NotificationTitle = styled.div`
  color: var(--text);
  font-weight: 600;
  font-size: 0.95rem;
`;

const NotificationMessage = styled.div`
  color: var(--text);
  font-size: 0.9rem;
  line-height: 1.4;
  margin-bottom: 0.75rem;
  opacity: 0.9;
`;

const NotificationActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const NotificationButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.25rem 0.75rem;
  border: none;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #E9B339, #FF7B5A);
    color: white;
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(233, 179, 57, 0.4);
    }
  ` : `
    background: transparent;
    color: var(--textSecondary);
    border: 1px solid var(--border);
    &:hover {
      background: var(--surface);
      color: var(--text);
    }
  `}
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 1.2rem;
  line-height: 1;
  opacity: 0.7;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
    color: var(--text);
    opacity: 1;
  }
`;

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  interface TimerState {
    remaining: number;
    hovered: boolean;
    focused: boolean;
    handle: ReturnType<typeof setTimeout> | null;
    lastStart: number;
  }
  const timerMap = useRef<Map<string, TimerState>>(new Map());

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
    const state = timerMap.current.get(id);
    if (state) {
      if (state.handle != null) clearTimeout(state.handle);
      timerMap.current.delete(id);
    }
  }, []);

  const pause = (id: string) => {
    const s = timerMap.current.get(id);
    if (s && s.handle != null) {
      const elapsed = Date.now() - s.lastStart;
      s.remaining = Math.max(0, s.remaining - elapsed);
      clearTimeout(s.handle);
      s.handle = null;
    }
  };

  const resume = (id: string) => {
    const s = timerMap.current.get(id);
    if (s && s.handle == null && !s.hovered && !s.focused) {
      if (s.remaining > 0) {
        s.lastStart = Date.now();
        s.handle = setTimeout(() => {
          removeNotification(id);
        }, s.remaining);
      } else {
        // Deadline passed while paused; expire immediately
        removeNotification(id);
      }
    }
  };

  const addNotification = useCallback((notification: Omit<Notification, 'id'>) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const newNotification: Notification = {
      id,
      duration: 5000,
      ...notification,
    };

    setNotifications(prev => [newNotification, ...prev]);

    if (newNotification.duration != null && newNotification.duration > 0) {
      const now = Date.now();
      timerMap.current.set(id, {
        remaining: newNotification.duration,
        hovered: false,
        focused: false,
        handle: setTimeout(() => {
          removeNotification(id);
        }, newNotification.duration),
        lastStart: now,
      });
    }
  }, [removeNotification]);

  const clearAll = useCallback(() => {
    timerMap.current.forEach((state) => {
      if (state.handle != null) clearTimeout(state.handle);
    });
    timerMap.current.clear();
    setNotifications([]);
  }, []);

  const showSuccess = useCallback((message: string, title?: string) => {
    addNotification({ type: 'success', message, title });
  }, [addNotification]);

  const showError = useCallback((message: string, title?: string) => {
    addNotification({ type: 'error', message, title });
  }, [addNotification]);

  const showWarning = useCallback((message: string, title?: string) => {
    addNotification({ type: 'warning', message, title });
  }, [addNotification]);

  const showInfo = useCallback((message: string, title?: string) => {
    addNotification({ type: 'info', message, title });
  }, [addNotification]);

  const value: NotificationContextType = {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  // Unmount cleanup: clear all pending timers
  useEffect(() => {
    const map = timerMap.current;
    return () => {
      map.forEach((state) => {
        if (state.handle != null) clearTimeout(state.handle);
      });
      map.clear();
    };
  }, [timerMap]);

  // Build the announcement text for a notification
  const announceText = (notification: Notification): string => {
    if (notification.title) {
      return `${notification.title}: ${notification.message}`;
    }
    return notification.message;
  };

  // Render polite/assertive announcement spans keyed by notification id, oldest first
  const politeAnnouncements = notifications
    .filter((n) => n.type !== 'error')
    .reverse();
  const assertiveAnnouncements = notifications
    .filter((n) => n.type === 'error')
    .reverse();

  return (
    <NotificationContext.Provider value={value}>
      <div aria-live="polite" data-testid="notification-polite-host" className="visually-hidden">
        {politeAnnouncements.map((n) => (
          <span key={n.id} aria-atomic="true" data-testid={`notification-polite-${n.id}`}>{announceText(n)}</span>
        ))}
      </div>
      <div aria-live="assertive" data-testid="notification-assertive-host" className="visually-hidden">
        {assertiveAnnouncements.map((n) => (
          <span key={n.id} aria-atomic="true" data-testid={`notification-assertive-${n.id}`}>{announceText(n)}</span>
        ))}
      </div>

      {children}

      <NotificationContainer>
        {notifications.map((notification) => {
          return (
            <NotificationItem
              key={notification.id}
              type={notification.type}
              data-testid={`toast-${notification.id}`}
              onMouseEnter={() => {
                const s = timerMap.current.get(notification.id);
                if (s) {
                  s.hovered = true;
                  pause(notification.id);
                }
              }}
              onMouseLeave={() => {
                const s = timerMap.current.get(notification.id);
                if (s) {
                  s.hovered = false;
                  resume(notification.id);
                }
              }}
              onFocus={() => {
                const s = timerMap.current.get(notification.id);
                if (s) {
                  s.focused = true;
                  pause(notification.id);
                }
              }}
              onBlur={(e) => {
                const s = timerMap.current.get(notification.id);
                if (s) {
                  // Ignore blur when focus is moving between controls inside this toast
                  if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget as Node)) {
                    return;
                  }
                  s.focused = false;
                  resume(notification.id);
                }
              }}
            >
              <NotificationHeader>
                <NotificationIcon type={notification.type}>
                  {notification.title && (
                    <NotificationTitle>{notification.title}</NotificationTitle>
                  )}
                </NotificationIcon>
                <CloseButton
                  type="button"
                  aria-label={`Dismiss notification: ${notification.title || notification.message}`}
                  onClick={() => removeNotification(notification.id)}
                >
                  <span aria-hidden="true">×</span>
                </CloseButton>
              </NotificationHeader>

              <NotificationMessage>{notification.message}</NotificationMessage>

              {notification.action && (
                <NotificationActions>
                  <NotificationButton
                    type="button"
                    variant="primary"
                    onClick={() => {
                      notification.action!.onClick();
                      removeNotification(notification.id);
                    }}
                  >
                    {notification.action.label}
                  </NotificationButton>
                  <NotificationButton
                    type="button"
                    onClick={() => removeNotification(notification.id)}
                  >
                    Dismiss
                  </NotificationButton>
                </NotificationActions>
              )}
            </NotificationItem>
          );
        })}
      </NotificationContainer>
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
